import type {
  ScheduleConflict,
  ScheduleConflictOccurrence,
  ScheduleRunSummary,
  ScheduleSlot,
  TeacherSchedule
} from '@/types'
import { HttpError } from '../request'

export type PageResponse<T> = {
  items: T[]
  total: number
  page: number
  pageSize: number
}

export type OperatorInfo = {
  id: number
  name?: string
}

export function toIsoString(value: unknown): string | null {
  if (typeof value !== 'string' || value.trim() === '') return null
  return value
}

export function isNotFoundError(error: unknown): boolean {
  return error instanceof HttpError && error.status === 404
}

export function toScheduleRunSummary(payload: unknown): ScheduleRunSummary | null {
  if (!payload || typeof payload !== 'object') return null
  const run = payload as Record<string, unknown>
  return {
    id: Number(run.id ?? 0),
    status: String(run.status ?? ''),
    timeLimitSeconds: run.timeLimitSeconds != null ? Number(run.timeLimitSeconds) : null,
    totalActivities: run.totalActivities != null ? Number(run.totalActivities) : null,
    maxPlacedActivities: run.maxPlacedActivities != null ? Number(run.maxPlacedActivities) : null,
    progress: Number(run.progress ?? 0),
    snapshotAvailable: Boolean(run.snapshotAvailable),
    lastSnapshotAt: toIsoString(run.lastSnapshotAt),
    startedAt: toIsoString(run.startedAt),
    finishedAt: toIsoString(run.finishedAt),
    queuePosition: run.queuePosition != null ? Number(run.queuePosition) : null
  }
}

export function toScheduleSlot(payload: unknown): ScheduleSlot {
  const slot = payload as Record<string, unknown>
  const courseName = String(slot.courseName ?? '')
  const teacherNames = Array.isArray(slot.teacherNames)
    ? slot.teacherNames.map((item) => String(item)).filter((name) => name !== '')
    : []
  const teacherName = teacherNames.length > 0 ? teacherNames.join('、') : String(slot.teacherName ?? '')
  const teacherIds = Array.isArray(slot.teacherIds)
    ? slot.teacherIds.map((item) => Number(item)).filter((id) => Number.isFinite(id) && id > 0)
    : []
  return {
    day: Number(slot.day ?? 0),
    period: Number(slot.period ?? 0),
    classId: slot.classId != null ? Number(slot.classId) : null,
    className: String(slot.className ?? ''),
    subgroupId: slot.subgroupId != null ? Number(slot.subgroupId) : null,
    subgroupName: slot.subgroupName != null ? String(slot.subgroupName) : null,
    courseId: slot.courseId != null ? Number(slot.courseId) : null,
    courseName,
    course: courseName,
    teacherId: slot.teacherId != null ? Number(slot.teacherId) : null,
    teacherName,
    teacher: teacherName,
    teacherIds: teacherIds.length > 0 ? teacherIds : null,
    teacherNames: teacherNames.length > 0 ? teacherNames : null,
    roomId: slot.roomId != null ? Number(slot.roomId) : null,
    roomName: slot.roomName != null ? String(slot.roomName) : null,
    duration: slot.duration != null ? Number(slot.duration) : undefined,
    weekStart: slot.weekStart != null ? Number(slot.weekStart) : null,
    weekEnd: slot.weekEnd != null ? Number(slot.weekEnd) : null,
    weekType: slot.weekType != null ? Number(slot.weekType) : null,
    taskActivityId: slot.taskActivityId != null ? Number(slot.taskActivityId) : null,
    occurrenceIndex: slot.occurrenceIndex != null ? Number(slot.occurrenceIndex) : null,
    occurrenceKey: typeof slot.occurrenceKey === 'string' ? slot.occurrenceKey : undefined,
    placed: typeof slot.placed === 'boolean' ? slot.placed : undefined,
    external: typeof slot.external === 'boolean' ? slot.external : false,
    sourceTaskId: slot.sourceTaskId != null ? Number(slot.sourceTaskId) : null,
    sourceTaskName: slot.sourceTaskName != null ? String(slot.sourceTaskName) : null
  }
}

export function toTeacherSchedule(payload: unknown): TeacherSchedule {
  const teacher = payload as Record<string, unknown>
  const teacherName = String(teacher.teacherName ?? '')
  const subjectNames = Array.isArray(teacher.subjectNames)
    ? teacher.subjectNames.map((item) => String(item))
    : []

  return {
    teacherId: teacher.teacherId != null ? Number(teacher.teacherId) : null,
    teacherName,
    name: teacherName,
    subject: subjectNames.join('、'),
    subjectNames,
    schedule: Array.isArray(teacher.schedule) ? teacher.schedule.map((item) => toScheduleSlot(item)) : []
  }
}

export function toScheduleConflictOccurrence(payload: unknown): ScheduleConflictOccurrence {
  const occurrence = payload as Record<string, unknown>
  return {
    taskActivityId: occurrence.taskActivityId != null ? Number(occurrence.taskActivityId) : null,
    occurrenceIndex: occurrence.occurrenceIndex != null ? Number(occurrence.occurrenceIndex) : null,
    day: occurrence.day != null ? Number(occurrence.day) : null,
    period: occurrence.period != null ? Number(occurrence.period) : null,
    roomId: occurrence.roomId != null ? Number(occurrence.roomId) : null
  }
}

export function toScheduleConflict(payload: unknown): ScheduleConflict {
  const conflict = payload as Record<string, unknown>
  return {
    source: String(conflict.source ?? ''),
    category: String(conflict.category ?? ''),
    severity: String(conflict.severity ?? ''),
    message: String(conflict.message ?? ''),
    snapshotId: conflict.snapshotId != null ? Number(conflict.snapshotId) : null,
    runId: conflict.runId != null ? Number(conflict.runId) : null,
    occurrences: Array.isArray(conflict.occurrences)
      ? conflict.occurrences.map((item) => toScheduleConflictOccurrence(item))
      : [],
    slots: Array.isArray(conflict.slots) ? conflict.slots.map((item) => toScheduleSlot(item)) : [],
    payload:
      conflict.payload && typeof conflict.payload === 'object'
        ? (conflict.payload as Record<string, unknown>)
        : undefined
  }
}

export function createOccurrenceKey(taskActivityId: number, occurrenceIndex: number): string {
  return `${taskActivityId}:${occurrenceIndex}`
}

export function buildClassOptionKey(classId: number, subgroupId: number | null | undefined): string {
  return `${classId}:${subgroupId ?? 'whole'}`
}

/**
 * 分组视图下的课时可见性（与班级课表一致）：
 * - 看全班：只显示全班共同课（subgroupId 为空）
 * - 看某一分组：显示该分组课 + 全班共同课
 */
export function isSubgroupVisibleForTarget(
  occurrenceSubgroupId: number | null | undefined,
  targetSubgroupId: number | null
): boolean {
  const normalizedOccurrenceSubgroupId = occurrenceSubgroupId ?? null
  if (targetSubgroupId == null) {
    return normalizedOccurrenceSubgroupId == null
  }
  return normalizedOccurrenceSubgroupId == null || normalizedOccurrenceSubgroupId === targetSubgroupId
}

export {
  compareClassNames,
  extractClassNumber,
  sortClassOptions,
  sortGradeOptions,
  buildGradeOrderMapFromGrades,
  buildGradeOrderMapFromClasses
} from '@/utils/academicOrder'
